import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Loader2, Sparkles, RefreshCw, X, Check, ImageIcon } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';
import { toast } from 'sonner';

/**
 * "Rewrite social draft with AI" — non-destructive.
 *
 *   1. Original draft on top (caption + image + hashtags, read-only).
 *   2. Refinement instructions textarea.
 *   3. Optional toggle: also regenerate the image. When on, the admin
 *      can override the image prompt explicitly.
 *   4. AI rewrite preview underneath (new caption + hashtags + maybe
 *      new image).
 *   5. Apply rewrite (persist to DB) or Reject (close, original untouched).
 */
export const SocialRewriteDialog = ({ draft, apiBase, open, onOpenChange, onApplied }) => {
  const [instructions, setInstructions] = useState('');
  const [regenImage, setRegenImage] = useState(false);
  const [imagePrompt, setImagePrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [applying, setApplying] = useState(false);
  const [aiDraft, setAiDraft] = useState(null);

  React.useEffect(() => {
    if (!open) {
      setInstructions('');
      setRegenImage(false);
      setImagePrompt('');
      setGenerating(false);
      setApplying(false);
      setAiDraft(null);
    }
  }, [open]);

  const handleGenerate = async () => {
    if (instructions.trim().length < 4) {
      toast.error('Please describe how the AI should rewrite this draft.');
      return;
    }
    setGenerating(true);
    const { data, error } = await apiFetch(`/api/admin/social/drafts/${draft.draft_id}/rewrite`, {
      method: 'POST',
      body: {
        draft_id: draft.draft_id,
        instructions: instructions.trim(),
        regenerate_image: regenImage,
        image_prompt: imagePrompt.trim() || null,
      },
    });
    setGenerating(false);
    if (error) {
      toast.error(error);
      return;
    }
    setAiDraft(data);
    toast.success(data?.image_url ? 'Rewrite + new image ready' : 'Rewrite ready');
  };

  const handleApply = async () => {
    if (!aiDraft) return;
    setApplying(true);
    const { data, error } = await apiFetch(`/api/admin/social/drafts/${draft.draft_id}/apply-rewrite`, {
      method: 'PATCH',
      body: {
        title: aiDraft.title,
        caption: aiDraft.caption,
        hashtags: aiDraft.hashtags,
        image_url: aiDraft.image_url || null,
      },
    });
    setApplying(false);
    if (error) {
      toast.error(error);
      return;
    }
    toast.success('Rewrite applied to draft');
    onApplied?.(draft.draft_id);
    onOpenChange(false);
  };

  if (!draft) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="social-rewrite-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#00A896]" />
            Rewrite social draft with AI
          </DialogTitle>
          <DialogDescription>
            Original stays safe — nothing is overwritten until you press Apply.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 px-1">
          {/* Original — read-only */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Original draft · {draft.platform} · {draft.format_label || draft.format}
              </Label>
              <Badge variant="outline" className="text-[10px]">read-only</Badge>
            </div>
            <div
              className="border rounded-md bg-slate-50 dark:bg-slate-900/40 dark:border-slate-700 p-3 flex gap-3"
              data-testid="social-rewrite-original"
            >
              {draft.image_url && (
                <img
                  src={`${apiBase}${draft.image_url}`}
                  alt="original"
                  className="w-24 h-24 object-cover rounded shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm mb-1">{draft.title}</h4>
                <p className="whitespace-pre-wrap text-sm text-foreground/80 max-h-[140px] overflow-y-auto">
                  {draft.caption}
                </p>
                {(draft.hashtags || []).length > 0 && (
                  <p className="text-xs text-[#00A896] mt-1 break-words">
                    {draft.hashtags.map((h) => `#${h}`).join(' ')}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="space-y-2">
            <Label htmlFor="social-rewrite-instructions" className="text-sm font-medium">
              How should the AI rewrite it?
            </Label>
            <Textarea
              id="social-rewrite-instructions"
              rows={3}
              placeholder='e.g. "Make it more direct and add a question at the end" or "Switch to a professional LinkedIn tone"'
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              data-testid="social-rewrite-instructions-input"
            />

            <div className="flex items-center justify-between gap-3 pt-1">
              <div className="flex items-center gap-2">
                <Switch
                  id="regen-image"
                  checked={regenImage}
                  onCheckedChange={setRegenImage}
                  data-testid="social-rewrite-regen-image-toggle"
                />
                <Label htmlFor="regen-image" className="text-sm cursor-pointer flex items-center gap-1">
                  <ImageIcon className="h-3 w-3" /> Also regenerate the image
                </Label>
              </div>
              <Button
                onClick={handleGenerate}
                disabled={generating || instructions.trim().length < 4}
                className="bg-[#00E5CC] text-[#0d1f2d] hover:bg-[#00d4bd]"
                data-testid="social-rewrite-generate-btn"
              >
                {generating
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Rewriting…</>
                  : aiDraft
                    ? <><RefreshCw className="h-4 w-4 mr-2" /> Generate again</>
                    : <><Sparkles className="h-4 w-4 mr-2" /> Generate rewrite</>}
              </Button>
            </div>

            {regenImage && (
              <div>
                <Label htmlFor="social-rewrite-img-prompt" className="text-xs">
                  Image prompt <span className="text-muted-foreground font-normal">(optional — overrides what the AI draws)</span>
                </Label>
                <Textarea
                  id="social-rewrite-img-prompt"
                  rows={2}
                  placeholder='e.g. "Calm pastel illustration of two people with thought bubbles, soft morning light"'
                  value={imagePrompt}
                  onChange={(e) => setImagePrompt(e.target.value)}
                  data-testid="social-rewrite-image-prompt-input"
                />
              </div>
            )}
          </div>

          {/* AI rewrite preview */}
          {aiDraft && (
            <div data-testid="social-rewrite-ai-preview">
              <Label className="text-xs font-semibold uppercase tracking-wide text-[#00A896] mb-1 block">
                AI rewrite preview
              </Label>
              <div className="border-2 border-[#00E5CC]/30 rounded-md bg-[#00E5CC]/5 p-3 flex gap-3">
                {(aiDraft.image_url || draft.image_url) && (
                  <img
                    src={`${apiBase}${aiDraft.image_url || draft.image_url}`}
                    alt="rewrite"
                    className="w-24 h-24 object-cover rounded shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-sm mb-1">{aiDraft.title}</h4>
                  <p className="whitespace-pre-wrap text-sm text-foreground max-h-[200px] overflow-y-auto">
                    {aiDraft.caption}
                  </p>
                  {(aiDraft.hashtags || []).length > 0 && (
                    <p className="text-xs text-[#00A896] mt-1 break-words">
                      {aiDraft.hashtags.map((h) => `#${h}`).join(' ')}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-row justify-between gap-2 pt-3 border-t">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="social-rewrite-reject-btn"
          >
            <X className="h-4 w-4 mr-1" /> Reject &amp; keep original
          </Button>
          <Button
            disabled={!aiDraft || applying}
            onClick={handleApply}
            className="bg-[#00A896] text-white hover:bg-[#008b7a]"
            data-testid="social-rewrite-apply-btn"
          >
            {applying
              ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Applying…</>
              : <><Check className="h-4 w-4 mr-1" /> Apply rewrite</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SocialRewriteDialog;
